// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Tukar Barang — Slice C, PREVIEW ONLY)
// version  : 1.1
// updated  : 2026-08-06
// protected: NO
// summary  : Alur Tukar Barang untuk Staff Store (defect-only, pengganti IDENTIK,
//            TANPA uang). Langkah: (1) scan/ketik barang cacat -> (2) centang
//            verifikasi -> (3) scan/ketik pengganti identik -> proses.
//            >7 hari: perlu persetujuan Kepala Cabang (alasan wajib). Anti klik-ganda.
//            v1.1 (UAT Owner): kolom QR bisa DIKETIK MANUAL (bukan hanya kamera),
//            tombol Batal/Reset, cegah QR lama==pengganti, tampilkan ALASAN tombol
//            mati, nomor langkah 1-2-3. Mobile-first. TIDAK menyentuh nota/billing.
// =============================================================================
import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Repeat, ScanLine, CheckCircle2, AlertTriangle, ShieldCheck, Loader2,
  PackageX, RotateCcw, Info, PackageCheck, X, Eraser, FileText, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { openPdf } from "@/v2/components/helpers";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { QrCameraButton } from "@/v2/components/QrCameraButton";
import { EmptyState, ListSkeleton, useDelayedLoading, apiErrorInfo } from "@/v2/components/ux";
import { ListPager, loadStoredPageSize } from "@/v2/components/ListPager";
import { parseQr } from "@/v2/components/QrBatchInput";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch (_) { return iso; }
};

// Kartu ringkas info item hasil scan (barang cacat / pengganti).
const ItemPreview = ({ info, loading, tone, testid }) => {
  if (loading) {
    return (
      <div className="rounded-xl border p-3 text-sm text-muted-foreground flex items-center gap-2" data-testid={`${testid}-loading`}>
        <Loader2 className="w-4 h-4 animate-spin" /> Memeriksa item…
      </div>
    );
  }
  if (!info) return null;
  const isMou = info.item_category === "MoU";
  return (
    <div
      className={`rounded-xl border p-3 space-y-1.5 ${tone === "old" ? "border-primary/30 bg-primary/5" : "border-[hsl(var(--badge-success))]/40 bg-[hsl(var(--badge-success))]/10"}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-foreground text-sm" data-testid={`${testid}-name`}>
          {info.product_name || "(produk tak dikenal)"}
        </span>
        <Badge variant={isMou ? "destructive" : "secondary"} data-testid={`${testid}-cat`}>
          {info.item_category}
        </Badge>
      </div>
      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
        <span>QR: <span className="font-mono">{info.qr_id}</span></span>
        <span>Size: <b className="text-foreground">{info.size}</b></span>
        <span>Klub: {info.club}</span>
        <span>Status: <b className="text-foreground">{info.current_state || "—"}</b></span>
        <span>Lokasi: {info.current_location || "—"}</span>
      </div>
    </div>
  );
};

export default function TukarPage() {
  const { api, me } = useV2Auth();
  const role = me?.role || "";
  const isKacab = role.startsWith("KACAB");

  const [oldQr, setOldQr] = useState("");
  const [newQr, setNewQr] = useState("");
  const [oldInfo, setOldInfo] = useState(null);
  const [newInfo, setNewInfo] = useState(null);
  const [oldLoading, setOldLoading] = useState(false);
  const [newLoading, setNewLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [needOverride, setNeedOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [result, setResult] = useState(null);

  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => loadStoredPageSize("exchanges"));
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const listTopRef = useRef(null);
  const showListSkeleton = useDelayedLoading(listLoading);

  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadList = useCallback(async (p = 1, size = pageSize) => {
    setListLoading(true);
    try {
      const r = await api(`/api/v2/exchanges?page=${p}&page_size=${size}`);
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        setList(data.items || []);
        setMeta({ total: data.total || 0, pages: data.pages || 1 });
        setPage(data.page || p);
      } else {
        setList([]);
      }
    } catch (_) {
      setList([]);
    } finally {
      setListLoading(false);
    }
  }, [api, pageSize]);

  useEffect(() => { loadList(1); }, [loadList]);

  const goPage = (p) => {
    loadList(p);
    if (listTopRef.current) listTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const changePageSize = (s) => { setPageSize(s); loadList(1, s); };

  const openDetail = useCallback(async (id) => {
    setDetailId(id); setDetail(null); setDetailLoading(true);
    try {
      const r = await api(`/api/v2/exchanges/${id}`);
      const d = await r.json().catch(() => ({}));
      if (r.ok) setDetail(d);
      else toast.error(d.detail || "Gagal memuat detail tukar");
    } catch (_) {
      toast.error("Gagal memuat detail tukar (jaringan)");
    } finally {
      setDetailLoading(false);
    }
  }, [api]);

  const lookup = useCallback(async (qr, which) => {
    const code = parseQr(qr);
    if (!code) return;
    const setLoading = which === "old" ? setOldLoading : setNewLoading;
    const setInfo = which === "old" ? setOldInfo : setNewInfo;
    setLoading(true);
    try {
      const r = await api(`/api/v2/scan/${encodeURIComponent(code)}`);
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setInfo(null);
        const info = apiErrorInfo(r, data, "QR tidak ditemukan di sistem.");
        toast.error(`Barang ${which === "old" ? "cacat" : "pengganti"}: ${info.message}`);
        return;
      }
      setInfo(data);
    } catch (e) {
      setInfo(null);
      toast.error(`Barang ${which === "old" ? "cacat" : "pengganti"}: gagal terhubung ke server (jaringan).`);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Set nilai QR (dari scan / ketik) + cegah QR lama == pengganti + abaikan kode sama.
  const applyCode = useCallback((rawCode, which) => {
    const code = parseQr(rawCode);
    if (!code) return;
    const otherRaw = which === "old" ? newQr : oldQr;
    const other = parseQr(otherRaw);
    // Tolak bila kode sama dengan kolom lain (barang cacat & pengganti tak boleh sama).
    if (other && other === code) {
      toast.error("QR barang cacat & pengganti tidak boleh sama.");
      return;
    }
    // Abaikan bila kode sama dengan yang sudah ada di kolom ini (hindari duplikasi/lookup ulang).
    const currentRaw = which === "old" ? oldQr : newQr;
    if (parseQr(currentRaw) === code) return;
    if (which === "old") setOldQr(code); else setNewQr(code);
    lookup(code, which);
  }, [oldQr, newQr, lookup]);

  const onScanOld = useCallback((code) => applyCode(code, "old"), [applyCode]);
  const onScanNew = useCallback((code) => applyCode(code, "new"), [applyCode]);

  // Ketik manual: lookup saat blur / Enter. Sinkron dgn pola input manual halaman lain.
  const onManualLookup = (which) => {
    const raw = which === "old" ? oldQr : newQr;
    if (!raw.trim()) return;
    const code = parseQr(raw);
    const otherRaw = which === "old" ? newQr : oldQr;
    if (code && parseQr(otherRaw) === code) {
      toast.error("QR barang cacat & pengganti tidak boleh sama.");
      return;
    }
    lookup(raw, which);
  };

  const clearField = (which) => {
    if (which === "old") { setOldQr(""); setOldInfo(null); }
    else { setNewQr(""); setNewInfo(null); }
    setErrorMsg("");
  };

  // RESET/BATAL menyeluruh: kosongkan kedua kolom, kartu detail, checkbox, override & error.
  // Catatan: halaman ini TIDAK menyimpan state di localStorage/context — seluruh state lokal,
  // jadi reset ini benar-benar mengembalikan ke keadaan awal.
  const resetForm = () => {
    setOldQr(""); setNewQr(""); setOldInfo(null); setNewInfo(null);
    setConfirmed(false); setNeedOverride(false); setOverrideReason("");
    setErrorMsg(""); setResult(null);
    setSubmitting(false);   // jaga-jaga: pastikan tombol tak tersangkut "memproses".
  };

  const sameQr = !!(oldQr.trim() && newQr.trim() && parseQr(oldQr) === parseQr(newQr));
  const anyFilled = !!(oldQr.trim() || newQr.trim() || oldInfo || newInfo || confirmed);

  // Daftar syarat yang BELUM terpenuhi -> ditampilkan agar user tak menebak kenapa tombol mati.
  const missing = [];
  if (!oldQr.trim()) missing.push("Isi / scan QR barang cacat");
  if (!newQr.trim()) missing.push("Isi / scan QR barang pengganti");
  if (sameQr) missing.push("QR pengganti tidak boleh sama dengan barang cacat");
  if (!confirmed) missing.push("Centang konfirmasi verifikasi barang cacat");
  if (needOverride && !isKacab) missing.push("Nota sudah lewat 7 hari — perlu persetujuan Kepala Cabang");
  if (needOverride && isKacab && !overrideReason.trim()) missing.push("Isi alasan persetujuan Kepala Cabang");

  const canSubmit = missing.length === 0 && !submitting;

  const submit = async () => {
    if (submitting) return;                                  // anti klik-ganda (guard)
    if (sameQr) { setErrorMsg("QR barang cacat & pengganti tidak boleh sama."); return; }
    if (!confirmed) {
      setErrorMsg("Centang konfirmasi verifikasi barang cacat terlebih dahulu.");
      return;
    }
    if (!oldQr.trim() || !newQr.trim()) {
      setErrorMsg("QR barang cacat & pengganti wajib diisi.");
      return;
    }
    if (needOverride && isKacab && !overrideReason.trim()) {
      setErrorMsg("Alasan persetujuan Kepala Cabang wajib diisi.");
      return;
    }
    setSubmitting(true);
    setErrorMsg("");
    const body = {
      old_qr: parseQr(oldQr),
      new_qr: parseQr(newQr),
      defect_confirmed: true,
    };
    if (needOverride && overrideReason.trim()) body.override_reason = overrideReason.trim();
    try {
      const r = await api("/api/v2/exchanges", { method: "POST", body: JSON.stringify(body) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const status = r.status;
        const detail = data.detail || "Gagal memproses tukar barang.";
        setErrorMsg(detail);
        // >7 hari: butuh Kepala Cabang (403) ATAU alasan override kosong (400).
        if ((status === 403 && /Kepala Cabang/i.test(detail)) ||
            (status === 400 && /override/i.test(detail))) {
          setNeedOverride(true);
        }
        if (status !== 403 && status !== 400) toast.error(detail);
        return;
      }
      toast.success(`Tukar berhasil — ${data.exchange_no}`);
      setResult(data);
      loadList(1);
    } catch (e) {
      setErrorMsg("Gagal terhubung ke server. Coba lagi.");
      toast.error("Gagal terhubung ke server. Coba lagi.");
    } finally {
      // WAJIB: apa pun yang terjadi (sukses/gagal/exception), lepas kunci tombol.
      setSubmitting(false);
    }
  };

  // ---------- SUCCESS SCREEN ----------
  if (result) {
    return (
      <div className="max-w-2xl mx-auto space-y-4" data-testid="v2-tukar-success">
        <Card>
          <CardContent className="pt-6 flex flex-col items-center text-center gap-3">
            <div className="grid place-items-center w-14 h-14 rounded-2xl bg-[hsl(var(--badge-success))]/15 text-[hsl(var(--badge-success))]">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Tukar Barang Berhasil</h3>
              <p className="text-sm text-muted-foreground">Nota asli & tagihan tidak berubah.</p>
            </div>
            <div className="w-full rounded-xl border p-4 text-sm space-y-2 text-left">
              <div className="flex justify-between"><span className="text-muted-foreground">No. Tukar</span><b data-testid="tukar-result-no">{result.exchange_no}</b></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Barang Cacat</span><b data-testid="tukar-result-retur">Tertahan di store</b></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Store</span><span>{result.store}</span></div>
              {result.within_7d === false && (
                <div className="flex justify-between"><span className="text-muted-foreground">Persetujuan</span><Badge variant="secondary">Override Kepala Cabang</Badge></div>
              )}
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5" /> Barang cacat tertahan di store (menunggu retur). Buat dokumen retur manual lewat menu Retur — boleh digabung, kapan saja.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 mt-1">
              <Button
                variant="outline"
                onClick={() => openPdf(api, "tukar", result.id, result.exchange_no)}
                data-testid="tukar-pdf-btn"
              >
                <FileText className="w-4 h-4 mr-1.5" /> Unduh Bukti Tukar (PDF)
              </Button>
              <Button onClick={resetForm} data-testid="tukar-again-btn">
                <Repeat className="w-4 h-4 mr-1.5" /> Tukar Lagi
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Baris input QR (dapat DIKETIK MANUAL + tombol Scan kamera + tombol bersihkan).
  const QrRow = ({ which, num, label, value, onChange, loading, camTestid, testid, placeholder }) => (
    <div className="space-y-2">
      <Label className="flex items-center gap-1.5" htmlFor={`${testid}-input`}>
        <span className="grid place-items-center w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold">{num}</span>
        {label}
      </Label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            id={`${testid}-input`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={() => onManualLookup(which)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onManualLookup(which); } }}
            placeholder={placeholder}
            className="font-mono pr-8"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            inputMode="text"
            data-testid={`${testid}-input`}
          />
          {value && (
            <button
              type="button"
              onClick={() => clearField(which)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Bersihkan kolom"
              data-testid={`${testid}-clear`}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <QrCameraButton onScan={which === "old" ? onScanOld : onScanNew} testid={camTestid} label="Scan" />
      </div>
      <ItemPreview info={which === "old" ? oldInfo : newInfo} loading={loading} tone={which} testid={`${testid}-preview`} />
    </div>
  );

  // ---------- FORM ----------
  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="v2-tukar-page">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Repeat className="w-5 h-5 text-primary" /> Tukar Barang
        </h2>
        <p className="text-sm text-muted-foreground">
          Khusus barang <b>cacat</b>. Pengganti harus <b>identik</b> (produk, ukuran, kategori sama). Tanpa uang, tanpa ubah nota.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base flex items-center gap-2">
            <ScanLine className="w-4 h-4" /> Proses Tukar
          </CardTitle>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={resetForm}
            disabled={!anyFilled && !errorMsg}
            data-testid="tukar-reset-btn"
            className="text-muted-foreground"
          >
            <Eraser className="w-4 h-4 mr-1.5" /> Batal / Reset
          </Button>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Langkah 1: barang cacat (ketik manual ATAU scan) */}
          {QrRow({
            which: "old", num: 1,
            label: "Scan / ketik QR barang cacat (dari nota penjualan)",
            value: oldQr, onChange: setOldQr, loading: oldLoading,
            camTestid: "tukar-old-cam", testid: "tukar-old-qr",
            placeholder: "Ketik atau scan QR barang cacat",
          })}

          {/* Langkah 2: konfirmasi verifikasi (WAJIB) */}
          <div className="rounded-xl border border-[hsl(var(--badge-warning))]/40 bg-[hsl(var(--badge-warning))]/10 p-3">
            <div className="flex items-start gap-3">
              <span className="grid place-items-center w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold mt-0.5 shrink-0">2</span>
              <Checkbox
                id="tukar-confirm"
                checked={confirmed}
                onCheckedChange={(v) => setConfirmed(v === true)}
                className="mt-0.5"
                data-testid="tukar-confirm-checkbox"
              />
              <Label htmlFor="tukar-confirm" className="text-sm leading-relaxed font-normal cursor-pointer text-[hsl(var(--badge-warning))]">
                <span className="flex items-center gap-1.5 font-medium mb-0.5">
                  <ShieldCheck className="w-4 h-4" /> Konfirmasi verifikasi (wajib)
                </span>
                Saya sudah memeriksa bukti foto/video dan memverifikasi barang ini cacat.
              </Label>
            </div>
          </div>

          {/* Langkah 3: barang pengganti (ketik manual ATAU scan) */}
          {QrRow({
            which: "new", num: 3,
            label: "Scan / ketik QR barang pengganti (identik, tersedia di store)",
            value: newQr, onChange: setNewQr, loading: newLoading,
            camTestid: "tukar-new-cam", testid: "tukar-new-qr",
            placeholder: "Ketik atau scan QR barang pengganti",
          })}

          {/* Peringatan: QR lama == pengganti */}
          {sameQr && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/5 text-destructive p-3 text-sm flex items-start gap-2" data-testid="tukar-same-qr-warning">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>QR barang cacat & pengganti <b>sama</b>. Pengganti harus barang berbeda yang identik.</span>
            </div>
          )}

          {/* Override >7 hari */}
          {needOverride && (
            <div className="rounded-xl border border-[hsl(var(--badge-warning))]/60 bg-[hsl(var(--badge-warning))]/10 p-3 space-y-2" data-testid="tukar-override-box">
              <div className="flex items-center gap-2 text-[hsl(var(--badge-warning))] font-medium text-sm">
                <AlertTriangle className="w-4 h-4" /> Sudah lewat 7 hari dari nota
              </div>
              {isKacab ? (
                <>
                  <p className="text-xs text-[hsl(var(--badge-warning))]/80">
                    Sebagai Kepala Cabang, Anda dapat menyetujui tukar ini. Alasan wajib diisi (tercatat untuk audit).
                  </p>
                  <Textarea
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder="Alasan persetujuan Kepala Cabang…"
                    rows={2}
                    data-testid="tukar-override-reason"
                  />
                </>
              ) : (
                <p className="text-xs text-[hsl(var(--badge-warning))]/80">
                  Perlu persetujuan <b>Kepala Cabang</b>. Minta Kepala Cabang memproses tukar ini.
                </p>
              )}
            </div>
          )}

          {/* Error inline */}
          {errorMsg && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/5 text-destructive p-3 text-sm flex items-start gap-2" data-testid="tukar-error">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* ALASAN tombol mati — supaya user tak menebak syarat mana yang belum terpenuhi */}
          {!canSubmit && !submitting && missing.length > 0 && (
            <div className="rounded-xl border border-[hsl(var(--badge-warning))]/40 bg-[hsl(var(--badge-warning))]/10 p-3 text-sm" data-testid="tukar-disabled-reasons">
              <div className="flex items-center gap-1.5 font-medium text-[hsl(var(--badge-warning))] mb-1.5">
                <Info className="w-4 h-4" /> Lengkapi dulu untuk bisa memproses:
              </div>
              <ul className="space-y-1">
                {missing.map((m, i) => (
                  <li key={i} className="flex items-center gap-2 text-[hsl(var(--badge-warning))]/90" data-testid="tukar-missing-item">
                    <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--warning))] shrink-0" />
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Separator />

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              size="lg"
              onClick={resetForm}
              disabled={submitting || (!anyFilled && !errorMsg)}
              data-testid="tukar-cancel-btn"
              className="sm:w-auto"
            >
              <Eraser className="w-4 h-4 mr-1.5" /> Batal
            </Button>
            <Button
              className="flex-1"
              size="lg"
              onClick={submit}
              disabled={!canSubmit}
              data-testid="tukar-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Memproses…</>
              ) : needOverride && isKacab ? (
                <><ShieldCheck className="w-4 h-4 mr-2" /> Proses dengan Persetujuan Kepala Cabang</>
              ) : (
                <><PackageCheck className="w-4 h-4 mr-2" /> Proses Tukar</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Riwayat tukar (region-aware, paginasi via ListPager, klik baris -> detail) */}
      <Card ref={listTopRef}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <RotateCcw className="w-4 h-4" /> Riwayat Tukar
            {meta.total > 0 && <span className="text-xs font-normal text-muted-foreground">({meta.total})</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {showListSkeleton ? (
            <ListSkeleton rows={4} testid="tukar-list-skeleton" />
          ) : list.length === 0 ? (
            <EmptyState
              icon={PackageX}
              title="Belum ada tukar barang"
              description="Riwayat tukar akan muncul di sini setelah Staff Store memproses barang cacat pertama."
              testid="tukar-empty"
            />
          ) : (
            <>
              <div className="space-y-2" data-testid="tukar-list">
                {list.map((x) => (
                  <button
                    key={x.id}
                    type="button"
                    onClick={() => openDetail(x.id)}
                    className="w-full text-left rounded-xl border p-3 flex items-center justify-between gap-3 transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
                    data-testid={`tukar-row-${x.exchange_no}`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{x.exchange_no}</div>
                      <div className="text-xs text-muted-foreground">{x.store} · {fmtDate(x.created_at)}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {x.within_7d === false && (
                        <Badge variant="warning">Override</Badge>
                      )}
                      <Badge variant={x.status === "COMPLETED" ? "secondary" : "outline"}>{x.status}</Badge>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </button>
                ))}
              </div>
              <ListPager
                page={page} pageSize={pageSize} total={meta.total} pages={meta.pages} loading={listLoading}
                onPageChange={goPage} onPageSizeChange={changePageSize}
                storageKey="exchanges" testid="tukar-pager"
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail tukar (klik baris) */}
      <Dialog open={!!detailId} onOpenChange={(o) => { if (!o) { setDetailId(null); setDetail(null); } }}>
        <DialogContent className="max-w-lg" data-testid="tukar-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="w-4 h-4 text-primary" /> Detail Tukar {detail?.exchange_no || ""}
            </DialogTitle>
            <DialogDescription>Bukti penukaran barang cacat. Nota asli tetap berlaku, tanpa transaksi uang.</DialogDescription>
          </DialogHeader>

          {detailLoading || !detail ? (
            <div className="py-8 flex items-center justify-center text-muted-foreground gap-2" data-testid="tukar-detail-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Memuat detail…
            </div>
          ) : (
            <div className="space-y-3 text-sm" data-testid="tukar-detail-body">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                <div><span className="text-muted-foreground">No. Tukar</span><div className="font-medium">{detail.exchange_no}</div></div>
                <div><span className="text-muted-foreground">Tanggal</span><div className="font-medium">{fmtDate(detail.created_at)}</div></div>
                <div><span className="text-muted-foreground">Store / Region</span><div className="font-medium">{detail.store} · {detail.region}</div></div>
                <div><span className="text-muted-foreground">Status</span><div className="font-medium">{detail.status}</div></div>
                <div className="col-span-2"><span className="text-muted-foreground">Nota Asli</span>
                  <div className="font-medium">{detail.invoice?.invoice_no || "-"}{detail.invoice?.txn_datetime ? ` · ${fmtDate(detail.invoice.txn_datetime)}` : ""}</div>
                </div>
              </div>

              <Separator />
              {[["DIKEMBALIKAN (cacat)", detail.old_item, "old"], ["PENGGANTI (identik)", detail.new_item, "new"]].map(([lbl, it, tone]) => (
                <div key={tone} className={`rounded-xl border p-3 space-y-1 ${tone === "old" ? "border-primary/30 bg-primary/5" : "border-[hsl(var(--badge-success))]/40 bg-[hsl(var(--badge-success))]/10"}`} data-testid={`tukar-detail-${tone}`}>
                  <div className="text-xs font-semibold text-muted-foreground">{lbl}</div>
                  <div className="font-medium">{it?.product_name || "-"}</div>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
                    <span>Klub: {it?.club || "-"}</span>
                    <span>Size: <b className="text-foreground">{it?.size || "-"}</b></span>
                    <span>Kategori: {it?.category || "-"}</span>
                  </div>
                  <div className="text-xs">KODE QR: <span className="font-mono font-medium text-foreground">{it?.qr_id || "-"}</span></div>
                </div>
              ))}

              <Separator />
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                <div><span className="text-muted-foreground">Staff Pemroses</span><div className="font-medium">{detail.staff?.name || "-"}</div></div>
                <div><span className="text-muted-foreground">Alasan</span><div className="font-medium">Barang cacat</div></div>
              </div>
              {detail.override && (
                <div className="rounded-xl border border-[hsl(var(--badge-warning))]/60 bg-[hsl(var(--badge-warning))]/10 p-3 text-xs" data-testid="tukar-detail-override">
                  <div className="font-medium text-[hsl(var(--badge-warning))] flex items-center gap-1.5 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Persetujuan Kepala Cabang (&gt;7 hari)
                  </div>
                  <div>Oleh: <b>{detail.override.name}</b></div>
                  <div>Alasan: {detail.override.reason || "-"}</div>
                </div>
              )}

              <Button className="w-full" variant="outline"
                onClick={() => openPdf(api, "tukar", detail.id, detail.exchange_no)}
                data-testid="tukar-detail-pdf-btn">
                <FileText className="w-4 h-4 mr-1.5" /> Unduh Bukti Tukar (PDF)
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
